if (!customElements.get('bastion-cross-sell')) {
  customElements.define(
    'bastion-cross-sell',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-cross-sell-track]');
        this.controls = this.querySelector('.bastion-cross-sell__controls');
        this.previousButton = this.querySelector('[data-cross-sell-previous]');
        this.nextButton = this.querySelector('[data-cross-sell-next]');
        this.addLabel = this.dataset.addLabel || 'Add';
        this.addedLabel = this.dataset.addedLabel || 'Added';
        this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';

        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        this.querySelectorAll('[data-cross-sell-card]').forEach((card) => {
          const variantField = card.querySelector('[data-cross-sell-variant]');
          const addButton = card.querySelector('[data-cross-sell-add]');
          const quantityInput = card.querySelector('[data-cross-sell-quantity-input]');
          const decreaseButton = card.querySelector('[data-cross-sell-quantity-decrease]');
          const increaseButton = card.querySelector('[data-cross-sell-quantity-increase]');

          variantField?.addEventListener('change', () => this.updateCard(card), { signal });
          addButton?.addEventListener('click', () => this.addCardToCart(card), { signal });
          decreaseButton?.addEventListener('click', () => this.changeQuantity(card, -1), { signal });
          increaseButton?.addEventListener('click', () => this.changeQuantity(card, 1), { signal });
          quantityInput?.addEventListener('input', () => this.updateQuantityControls(card, false), { signal });
          quantityInput?.addEventListener('change', () => this.updateQuantityControls(card), { signal });
          quantityInput?.addEventListener('blur', () => this.updateQuantityControls(card), { signal });
          this.updateCard(card);
          this.updateQuantityControls(card);
        });

        this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.move(1), { signal });
        this.track?.addEventListener('scroll', () => this.updateControls(), { passive: true, signal });
        window.addEventListener('resize', () => this.updateControls(), { passive: true, signal });

        document.addEventListener('cart:change', (event) => {
          this.refreshCartVisibility(event.detail?.cart);
        }, { signal });
        document.addEventListener('cart:refresh', () => {
          this.refreshCartVisibility();
        }, { signal });

        this.refreshCartVisibility();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      async refreshCartVisibility(cart) {
        const requestId = (this.cartVisibilityRequestId || 0) + 1;
        this.cartVisibilityRequestId = requestId;

        try {
          let currentCart = cart;

          if (!currentCart?.items) {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
              credentials: 'same-origin',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
            });

            if (!response.ok) throw new Error('The cart could not be loaded.');
            currentCart = await response.json();
          }

          if (requestId !== this.cartVisibilityRequestId) return;
          this.applyCartVisibility(currentCart);
        } catch (error) {
          if (requestId !== this.cartVisibilityRequestId) return;

          this.querySelectorAll('[data-cross-sell-card]').forEach((card) => {
            card.hidden = false;
            card.removeAttribute('data-in-cart');
          });
          this.updateVisibleLayout();
        }
      }

      applyCartVisibility(cart) {
        const productIdsInCart = new Set(
          (cart?.items || []).map((item) => String(item.product_id)),
        );

        this.querySelectorAll('[data-cross-sell-card]').forEach((card) => {
          const isInCart = productIdsInCart.has(String(card.dataset.productId));
          card.hidden = isInCart;
          card.toggleAttribute('data-in-cart', isInCart);
        });

        this.updateVisibleLayout();
      }

      updateVisibleLayout() {
        const visibleCards = Array.from(this.querySelectorAll('[data-cross-sell-card]')).filter(
          (card) => !card.hidden,
        );

        this.dataset.visibleCount = String(visibleCards.length);
        this.hidden = visibleCards.length === 0;

        if (this.track) this.track.scrollLeft = 0;

        if (visibleCards.length === 0) {
          if (this.controls) this.controls.hidden = true;
          return;
        }

        requestAnimationFrame(() => this.updateControls());
      }

      getVariantData(field) {
        if (!field) return null;
        if (field.tagName === 'SELECT') {
          return field.selectedOptions[0] || null;
        }
        return field;
      }

      getQuantity(card, normalize = true) {
        const input = card.querySelector('[data-cross-sell-quantity-input]');
        if (!input) return 1;

        const parsedValue = Number.parseInt(input.value, 10);
        const quantity = Number.isFinite(parsedValue) ? Math.max(1, parsedValue) : 1;
        if (normalize) input.value = String(quantity);
        return quantity;
      }

      changeQuantity(card, amount) {
        const input = card.querySelector('[data-cross-sell-quantity-input]');
        if (!input) return;

        input.value = String(Math.max(1, this.getQuantity(card) + amount));
        this.updateQuantityControls(card);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateQuantityControls(card, normalize = true) {
        const input = card.querySelector('[data-cross-sell-quantity-input]');
        const decreaseButton = card.querySelector('[data-cross-sell-quantity-decrease]');
        if (!input) return;

        const parsedValue = Number.parseInt(input.value, 10);
        if (normalize) {
          input.value = String(Number.isFinite(parsedValue) ? Math.max(1, parsedValue) : 1);
        }

        const currentQuantity = Number.parseInt(input.value, 10);
        if (decreaseButton) decreaseButton.disabled = !Number.isFinite(currentQuantity) || currentQuantity <= 1;
      }

      updateCard(card) {
        const field = card.querySelector('[data-cross-sell-variant]');
        const data = this.getVariantData(field);
        if (!data) return;

        const discount = Number(card.dataset.discount || 0);
        const isAvailable = data.dataset.available === 'true';
        const beforePrice = card.querySelector('[data-cross-sell-before-price]');
        const currentPrice = card.querySelector('[data-cross-sell-current-price]');
        const savings = card.querySelector('[data-cross-sell-savings]');
        const addButton = card.querySelector('[data-cross-sell-add]');
        const buttonLabel = card.querySelector('[data-cross-sell-button-label]');
        const image = card.querySelector('.bastion-cross-sell__image:not(.bastion-cross-sell__image--placeholder)');
        const naturalSavingsPercent = Number(data.dataset.naturalSavingsPercent || 0);

        if (discount > 0) {
          beforePrice.hidden = false;
          beforePrice.textContent = data.dataset.price || '';
          currentPrice.textContent = data.dataset.offerPrice || data.dataset.price || '';
          savings.hidden = false;
          savings.textContent = `${discount}% savings · ${data.dataset.customSavings || ''}`;
        } else if (naturalSavingsPercent > 0) {
          beforePrice.hidden = false;
          beforePrice.textContent = data.dataset.comparePrice || '';
          currentPrice.textContent = data.dataset.price || '';
          savings.hidden = false;
          savings.textContent = `${naturalSavingsPercent}% savings`;
        } else {
          beforePrice.hidden = true;
          beforePrice.textContent = '';
          currentPrice.textContent = data.dataset.price || '';
          savings.hidden = true;
          savings.textContent = '';
        }

        if (image && data.dataset.imageUrl) {
          image.src = data.dataset.imageUrl;
          image.removeAttribute('srcset');
        }

        if (addButton) {
          addButton.disabled = !isAvailable;
          addButton.removeAttribute('aria-busy');
        }
        if (buttonLabel) buttonLabel.textContent = isAvailable ? this.addLabel : this.soldOutLabel;
      }

      move(direction) {
        if (!this.track) return;
        const firstCard = this.track.querySelector('[data-cross-sell-card]:not([hidden])');
        const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || 0);
        const distance = (firstCard?.getBoundingClientRect().width || this.track.clientWidth) + gap;
        this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      }

      updateControls() {
        if (!this.track) return;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        if (this.controls) this.controls.hidden = maxScroll <= 2;
        if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2 || maxScroll <= 2;
      }

      async addCardToCart(card) {
        const field = card.querySelector('[data-cross-sell-variant]');
        const data = this.getVariantData(field);
        const addButton = card.querySelector('[data-cross-sell-add]');
        const buttonLabel = card.querySelector('[data-cross-sell-button-label]');
        const status = card.querySelector('[data-cross-sell-card-status]');
        const discount = Number(card.dataset.discount || 0);
        const quantity = this.getQuantity(card);

        if (!data || data.dataset.available !== 'true' || addButton?.getAttribute('aria-busy') === 'true') return;

        addButton?.setAttribute('aria-busy', 'true');
        if (status) {
          status.textContent = '';
          status.removeAttribute('data-error');
        }

        const sectionsToBundle = new Set();
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }));
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        const properties = { '_Bastion cross-sell': 'Yes' };
        if (discount > 0) properties['_Bastion cross-sell discount'] = `${discount}%`;

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [{ id: field.value, quantity, properties }],
              sections: Array.from(sectionsToBundle).join(','),
            }),
          });
          const responseJson = await response.json();

          if (!response.ok) {
            throw new Error(responseJson.description || responseJson.message || 'This item could not be added to the cart.');
          }

          if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
            window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
            return;
          }

          const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((cartResponse) => cartResponse.json());
          cart.sections = responseJson.sections;
          const addedItems = responseJson.items || [];

          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: addedItems, cart },
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart },
          }));

          if (buttonLabel) buttonLabel.textContent = this.addedLabel;
          if (status) status.textContent = this.addedLabel;
          window.setTimeout(() => {
            if (buttonLabel) buttonLabel.textContent = this.addLabel;
            if (status) status.textContent = '';
          }, 1800);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'This item could not be added to the cart.';
          if (status) {
            status.dataset.error = '';
            status.textContent = message;
          }
          document.dispatchEvent(new CustomEvent('toast:show', { detail: { message, tone: 'error' } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } finally {
          addButton?.removeAttribute('aria-busy');
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    },
  );
}
